import { useState, useEffect } from "react";

const PENDING_RECIPIENTS_KEY = "pendingNotificationRecipients";

const savePendingRecipients = (recipientIds: string[]) => {
  localStorage.setItem(PENDING_RECIPIENTS_KEY, JSON.stringify(recipientIds));
};

const getPendingRecipients = (): string[] => {
  try {
    const stored = localStorage.getItem(PENDING_RECIPIENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const clearPendingRecipients = () => {
  localStorage.removeItem(PENDING_RECIPIENTS_KEY);
};

/**
 * Custom hook for managing pending notification recipients
 * Handles localStorage persistence and state management
 */
export const usePendingRecipients = (
  initialRecipients: string[] = [],
  shouldLoadFromStorage = false,
) => {
  const [pendingRecipients, setPendingRecipientsState] = useState<string[]>(
    () => {
      if (initialRecipients.length > 0) {
        return initialRecipients;
      }

      // This is for scenarios where we want to restore a draft notification
      if (shouldLoadFromStorage) {
        const stored = getPendingRecipients();
        return stored;
      }

      return [];
    },
  );

  useEffect(() => {
    savePendingRecipients(pendingRecipients);
  }, [pendingRecipients]);

  const setPendingRecipients = (
    recipients: string[] | ((prev: string[]) => string[]),
  ) => {
    setPendingRecipientsState(recipients);
  };

  const addRecipient = (customerId: string) => {
    setPendingRecipientsState((prev) => {
      if (prev.includes(customerId)) return prev;
      return [...prev, customerId];
    });
  };

  const removeRecipient = (customerId: string) => {
    setPendingRecipientsState((prev) => prev.filter((id) => id !== customerId));
  };

  const clearRecipients = () => {
    setPendingRecipientsState([]);
    clearPendingRecipients();
  };

  const hasRecipients = pendingRecipients.length > 0;

  return {
    pendingRecipients,
    setPendingRecipients,
    addRecipient,
    removeRecipient,
    clearRecipients,
    hasRecipients,
  };
};
